import React, { useState, useEffect, useRef, KeyboardEvent, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useClinic } from '@/contexts/ClinicContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Loader2, Send, MessageSquare, Plus, RefreshCw, Link2, Mail, Phone, Check, Search, User, Wifi, WifiOff, BellRing } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import api from '@/services/api';
import { patientService } from '@/services/patientService';
import { Client as TwilioClient, Conversation as TwilioConversation, Message as TwilioMessage, Participant } from '@twilio/conversations';
import { formatDOB } from '@/lib/dateUtils';
import { API_SERVER_URL } from '@/lib/apiConfig';

// Use the main API for conversations (messaging is part of the main API)
const API_BASE_URL = API_SERVER_URL;

interface Conversation {
  /** New patient-messaging uses `id`; keep conversationId for compat */
  id?: string;
  conversationId: string;
  twilioSid?: string;
  type: string;
  status: string;
  createdAt: string;
  clinicId: string;
  lastMessageAt?: string;
  patientId?: string;
  patientName?: string;
  patientEmail?: string;
  patientPhone?: string;
  /** True when the provider joined an existing shared conversation */
  joined?: boolean;
}

interface Message {
  messageId: string;
  body: string;
  authorId: string;
  authorType?: string;
  senderName?: string;
  senderRole?: string;
  createdAt: string;
  conversationId: string;
}

// Deterministic color per authorId
const msgUserColors = [
  { bg: 'bg-teal-100', text: 'text-teal-700', name: 'text-teal-600', bubble: 'bg-teal-600 text-white' },
  { bg: 'bg-blue-100', text: 'text-blue-700', name: 'text-blue-600', bubble: 'bg-blue-600 text-white' },
  { bg: 'bg-purple-100', text: 'text-purple-700', name: 'text-purple-600', bubble: 'bg-purple-600 text-white' },
  { bg: 'bg-pink-100', text: 'text-pink-700', name: 'text-pink-600', bubble: 'bg-pink-500 text-white' },
  { bg: 'bg-orange-100', text: 'text-orange-700', name: 'text-orange-600', bubble: 'bg-orange-500 text-white' },
  { bg: 'bg-indigo-100', text: 'text-indigo-700', name: 'text-indigo-600', bubble: 'bg-indigo-600 text-white' },
  { bg: 'bg-emerald-100', text: 'text-emerald-700', name: 'text-emerald-600', bubble: 'bg-emerald-600 text-white' },
  { bg: 'bg-rose-100', text: 'text-rose-700', name: 'text-rose-600', bubble: 'bg-rose-500 text-white' },
];

function getMsgColorForAuthor(authorId: string) {
  let hash = 0;
  for (let i = 0; i < authorId.length; i++) {
    hash = ((hash << 5) - hash + authorId.charCodeAt(i)) | 0;
  }
  return msgUserColors[Math.abs(hash) % msgUserColors.length];
}

function getMsgInitials(name?: string) {
  if (!name) return '?';
  const parts = name.split(' ').filter(n => n);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

/** Helper: get canonical conversation ID (works for old and new shapes) */
function getConvId(conv: Conversation): string {
  return conv.id || conv.conversationId || '';
}

interface Patient {
  id: string;
  full_name: string;
  email: string;
  phone?: string;
  dob?: string;
  gender?: string;
}

const Messaging = () => {
  const { user } = useAuth();
  const { clinic, isPractitioner, isAdmin } = useClinic();
  const { toast } = useToast();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [selectedPatientDefaultEmail, setSelectedPatientDefaultEmail] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [moduleStatus, setModuleStatus] = useState<any>(null);
  
  // Twilio real-time connection (direct SDK — mirrors PatientMessagingTab)
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'connecting' | 'disconnected' | 'failed' | 'error'>('disconnected');
  const isConnected = connectionStatus === 'connected';
  const [typingParticipants, setTypingParticipants] = useState<string[]>([]);
  const twilioClientRef = useRef<TwilioClient | null>(null);
  const twilioConvRef = useRef<TwilioConversation | null>(null);
  const twilioUnsubscribeRef = useRef<(() => void) | null>(null);
  
  // Send Link Dialog state
  const [showSendLinkDialog, setShowSendLinkDialog] = useState(false);
  const [sendLinkChannel, setSendLinkChannel] = useState<'email' | 'sms' | 'both'>('email');
  const [sendingLink, setSendingLink] = useState(false);
  const [linkSent, setLinkSent] = useState(false);

  // New Conversation Dialog state
  const [showNewConversationDialog, setShowNewConversationDialog] = useState(false);
  const [patientSearch, setPatientSearch] = useState('');
  const [searchResults, setSearchResults] = useState<Patient[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [searchingPatients, setSearchingPatients] = useState(false);
  const [creatingConversation, setCreatingConversation] = useState(false);

  // Unread counts (per-conversation)
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
  // Track which patients are online (keyed by conversation ID)
  const [onlinePatients, setOnlinePatients] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Get connection status display
  const getConnectionStatusDisplay = useCallback((status: string) => {
    switch (status) {
      case 'connected':
        return { label: 'Connected', color: 'bg-emerald-500', icon: Wifi };
      case 'connecting':
        return { label: 'Connecting...', color: 'bg-yellow-500', icon: Wifi };
      case 'disconnected':
      case 'failed':
      case 'error':
        return { label: 'Disconnected', color: 'bg-red-500', icon: WifiOff };
      default:
        return { label: 'Unknown', color: 'bg-gray-500', icon: Wifi };
    }
  }, []);

  // Cleanup Twilio client on unmount
  useEffect(() => {
    return () => {
      twilioClientRef.current?.shutdown();
      twilioClientRef.current = null;
      twilioConvRef.current = null;
    };
  }, []);

  // Check module status
  useEffect(() => {
    checkModuleStatus();
  }, []);

  // Auto-scroll to bottom when messages change
  const prevMessageCountRef = useRef(0);
  useEffect(() => {
    if (messages.length === 0) return;
    const isInitialLoad = prevMessageCountRef.current === 0;
    prevMessageCountRef.current = messages.length;
    if (isInitialLoad) {
      setTimeout(() => {
        const el = messagesEndRef.current;
        if (el) {
          const container = el.closest('.overflow-y-auto.p-3') as HTMLElement;
          if (container) container.scrollTop = container.scrollHeight;
        }
      }, 100);
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Subscribe to real-time messages when conversation is selected
  useEffect(() => {
    if (!selectedConversation?.twilioSid) {
      // No Twilio SID - load from API as fallback
      if (selectedConversation) {
        loadMessages(getConvId(selectedConversation));
      }
      return;
    }

    // Always load messages from REST first
    setLoadingMessages(true);
    loadMessages(getConvId(selectedConversation));

    if (!selectedConversation.twilioSid) return;

    let cancelled = false;

    const setupRealtimeSubscription = async () => {
      try {
        setConnectionStatus('connecting');
        twilioUnsubscribeRef.current?.();

        // Ensure participant in Twilio (adds staff to Twilio conversation)
        await api.post(
          `/patient-messaging/${getConvId(selectedConversation)}/ensure-participant`,
          {},
          { headers: { 'x-selected-clinic-id': selectedConversation.clinicId || clinic?.id } },
        );

        // Get a Twilio token using the patient-messaging endpoint
        const tokenRes = await api.get('/patient-messaging/twilio-token', {
          headers: { 'x-selected-clinic-id': selectedConversation.clinicId || clinic?.id },
        });
        const { token: twilioToken } = tokenRes.data;

        if (cancelled) return;

        // Create a local Twilio Client
        if (!twilioClientRef.current) {
          const client = new TwilioClient(twilioToken);
          await new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(() => reject(new Error('Twilio init timeout')), 15000);
            client.on('stateChanged', (state: string) => {
              if (state === 'initialized') { clearTimeout(timeout); resolve(); }
              if (state === 'failed') { clearTimeout(timeout); reject(new Error('Twilio init failed')); }
            });
          });
          if (cancelled) { client.shutdown(); return; }
          twilioClientRef.current = client;
        } else {
          await twilioClientRef.current.updateToken(twilioToken);
        }

        // Subscribe to the conversation
        const conv = await twilioClientRef.current.getConversationBySid(selectedConversation.twilioSid!);
        twilioConvRef.current = conv;

        if (cancelled) return;
        setConnectionStatus('connected');

        // Listen for new messages
        const onMessageAdded = (msg: TwilioMessage) => {
          // Extract sender info from Twilio message attributes
          let senderName = '';
          let senderRole = '';
          let authorType = '';
          try {
            const attrs = typeof msg.attributes === 'string'
              ? JSON.parse(msg.attributes)
              : (msg.attributes as Record<string, unknown>) || {};
            if (attrs.authorName) senderName = String(attrs.authorName);
            if (attrs.authorRole) senderRole = String(attrs.authorRole);
            if (attrs.authorType) authorType = String(attrs.authorType);
          } catch { /* skip */ }

          const author = msg.author || '';
          if (!authorType) {
            authorType = author.includes('patient:') ? 'patient' : 'staff';
          }

          const newMsg: Message = {
            messageId: msg.sid,
            body: msg.body || '',
            authorId: author,
            authorType,
            senderName,
            senderRole,
            createdAt: msg.dateCreated?.toISOString() || new Date().toISOString(),
            conversationId: getConvId(selectedConversation),
          };
          setMessages(prev => {
            if (prev.some(m => m.messageId === newMsg.messageId)) return prev;
            const optimisticIndex = prev.findIndex(
              m => m.messageId.startsWith('temp-') && m.body === newMsg.body
            );
            if (optimisticIndex !== -1) {
              const updated = [...prev];
              updated[optimisticIndex] = newMsg;
              return updated.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
            }
            return [...prev, newMsg].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
          });

          // Auto-mark as read since provider is actively viewing this conversation
          markConversationAsRead(getConvId(selectedConversation));
        };
        conv.on('messageAdded', onMessageAdded);

        // Typing indicators
        const onTypingStarted = (p: Participant) => {
          const pid = p.identity || p.sid;
          setTypingParticipants(prev => (prev.includes(pid) ? prev : [...prev, pid]));
        };
        const onTypingEnded = (p: Participant) => {
          const pid = p.identity || p.sid;
          setTypingParticipants(prev => prev.filter(x => x !== pid));
        };
        conv.on('typingStarted', onTypingStarted);
        conv.on('typingEnded', onTypingEnded);

        twilioUnsubscribeRef.current = () => {
          conv.removeListener('messageAdded', onMessageAdded);
          conv.removeListener('typingStarted', onTypingStarted);
          conv.removeListener('typingEnded', onTypingEnded);
        };
      } catch (error) {
        console.error('[Messaging] Failed to setup real-time subscription:', error);
        if (!cancelled) setConnectionStatus('failed');
      }
    };

    setupRealtimeSubscription();

    return () => {
      cancelled = true;
      twilioUnsubscribeRef.current?.();
      twilioUnsubscribeRef.current = null;
    };
  }, [selectedConversation?.twilioSid, selectedConversation?.conversationId, clinic?.id]);

  // ──── Check patient online status via Twilio reachability ────
  useEffect(() => {
    if (!twilioClientRef.current || connectionStatus !== 'connected' || conversations.length === 0) return;

    let cancelled = false;
    const userCleanups: (() => void)[] = [];

    const checkOnlineStatus = async () => {
      const client = twilioClientRef.current;
      if (!client) return;

      for (const conv of conversations) {
        if (cancelled || !conv.twilioSid) continue;
        try {
          const twilioConv = await client.getConversationBySid(conv.twilioSid);
          const participants = await twilioConv.getParticipants();
          const convId = getConvId(conv);

          for (const participant of participants) {
            const identity = participant.identity || '';
            // Patient identities contain 'patient:'
            if (!identity.includes('patient:')) continue;

            try {
              const user = await client.getUser(identity);
              if (cancelled) return;

              // Set initial status
              if (user.isOnline) {
                setOnlinePatients(prev => {
                  const next = new Set(prev);
                  next.add(convId);
                  return next;
                });
              }

              // Listen for real-time status changes
              const onUserUpdated = (data: { user: any; updateReasons: string[] }) => {
                if (data.updateReasons.includes('reachabilityOnline')) {
                  setOnlinePatients(prev => {
                    const next = new Set(prev);
                    if (data.user.isOnline) {
                      next.add(convId);
                    } else {
                      next.delete(convId);
                    }
                    return next;
                  });
                }
              };
              user.on('updated', onUserUpdated);
              userCleanups.push(() => user.removeListener('updated', onUserUpdated));
            } catch {
              // User reachability may not be enabled — skip silently
            }
            break; // Only need one patient per conversation
          }
        } catch {
          // Skip conversations we can't access
        }
      }
    };

    checkOnlineStatus();

    return () => {
      cancelled = true;
      userCleanups.forEach(fn => fn());
    };
  }, [connectionStatus, conversations]);

  // Resolve selected patient's default-marked email from patient contacts
  useEffect(() => {
    let isMounted = true;

    const resolveDefaultPatientEmail = async () => {
      setSelectedPatientDefaultEmail(null);

      const patientId = selectedConversation?.patientId;
      if (!patientId) return;

      try {
        const response = await patientService.getPatientById(patientId);
        const patient = response?.data;
        const contacts = Array.isArray(patient?.contacts) ? patient.contacts : [];
        const emailContacts = contacts.filter((contact: any) => contact?.key === 'email' && contact?.value);

        const defaultEmail = emailContacts.find((contact: any) => contact?.is_default)?.value;
        const fallbackEmail = emailContacts[0]?.value;
        const resolvedEmail = defaultEmail || fallbackEmail || patient?.email || null;

        if (isMounted) {
          setSelectedPatientDefaultEmail(resolvedEmail);
        }
      } catch (error) {
        if (isMounted) {
          setSelectedPatientDefaultEmail(null);
        }
      }
    };

    void resolveDefaultPatientEmail();

    return () => {
      isMounted = false;
    };
  }, [selectedConversation?.patientId]);

  // Handle keyboard shortcuts for message input
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter without Shift sends the message
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
    // Shift+Enter adds a new line (default behavior)
  };

  // Auto-resize textarea and send typing indicator
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewMessage(e.target.value);
    // Auto-resize
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`;
    }
    
    // Send typing indicator if connected via Twilio
    if (selectedConversation?.twilioSid && isConnected && twilioConvRef.current) {
      twilioConvRef.current.typing();
    }
  };

  const checkModuleStatus = async () => {
    try {
      const response = await api.get('/messaging/status');
      setModuleStatus({ status: 'ok', ...response.data });
    } catch (error) {
      console.error('Module status check failed:', error);
      setModuleStatus({ status: 'error', message: 'Could not connect to messaging service' });
    }
  };

  // Fetch per-conversation unread counts
  const fetchUnreadCounts = useCallback(async () => {
    try {
      const response = await api.get('/patient-messaging/unread-counts', {
        headers: { 'x-selected-clinic-id': clinic?.id },
      });
      setUnreadCounts(response.data?.perConversation || {});
    } catch (error) {
      console.error('Failed to fetch unread counts:', error);
    }
  }, []);

  // Poll unread counts every 15 seconds
  useEffect(() => {
    fetchUnreadCounts();
    const interval = setInterval(fetchUnreadCounts, 15_000);
    return () => clearInterval(interval);
  }, [fetchUnreadCounts]);

  // Mark a conversation as read and refresh unread counts
  const markConversationAsRead = useCallback(async (conversationId: string) => {
    try {
      await api.patch(`/patient-messaging/${conversationId}/read`, {}, {
        headers: { 'x-selected-clinic-id': clinic?.id },
      });
      // Optimistically clear the local unread count
      setUnreadCounts(prev => {
        const updated = { ...prev };
        delete updated[conversationId];
        return updated;
      });
      // Invalidate the sidebar badge query so it refreshes
      queryClient.invalidateQueries({ queryKey: ['messagingUnreadCount'] });
    } catch (error) {
      console.error('Failed to mark conversation as read:', error);
    }
  }, [queryClient]);

  // Load conversations — uses the clinic-scoped shared inbox so every
  // provider in the clinic sees all patient conversations.
  const loadConversations = async (clinicIdOverride?: string) => {
    setLoading(true);
    try {
      const activeClinicId = clinicIdOverride || clinic?.id;
      const params: Record<string, string> = {};
      if (activeClinicId) {
        params.clinicId = activeClinicId;
      }
      const response = await api.get('/patient-messaging', {
        params,
        headers: { 'x-selected-clinic-id': activeClinicId },
      });
      const allConversations = response.data.conversations || [];
      setConversations(allConversations);
    } catch (error: any) {
      console.error('Failed to load conversations:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to load conversations',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Load messages for a conversation
  const loadMessages = async (conversationId: string) => {
    try {
      const response = await api.get(`/patient-messaging/${conversationId}/messages`, {
        headers: { 'x-selected-clinic-id': clinic?.id },
      });
      const loadedMessages: Message[] = (response.data.messages || []).map((msg: any) => ({
        messageId: msg.id || msg.messageId,
        body: msg.body || '',
        authorId: msg.author_id || msg.authorId || '',
        authorType: msg.author_type || msg.authorType || '',
        senderName: msg.sender_name || msg.senderName || '',
        senderRole: msg.sender_role || msg.senderRole || '',
        createdAt: msg.created_at || msg.createdAt || '',
        conversationId,
      }));
      const sortedMessages = loadedMessages.sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
      setMessages(sortedMessages);
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  // Search patients (empty query returns initial 100 patients)
  const searchPatients = async (query: string) => {
    setSearchingPatients(true);
    try {
      const clinicId = clinic?.id || '';
      const response = await patientService.getPatientsForDropdown(query, clinicId);
      setSearchResults(response.data?.patients || []);
    } catch (error) {
      console.error('Failed to search patients:', error);
      setSearchResults([]);
    } finally {
      setSearchingPatients(false);
    }
  };

  // Load patients when dialog opens or search changes
  useEffect(() => {
    if (!showNewConversationDialog || !clinic?.id) return;
    
    // For initial load (empty search), load immediately
    // For search queries, debounce
    if (patientSearch === '') {
      searchPatients('');
    } else {
      const timer = setTimeout(() => {
        searchPatients(patientSearch);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [showNewConversationDialog, clinic?.id, patientSearch]);

  // Create a new conversation with selected patient
  const createConversation = async () => {
    if (!selectedPatient) {
      toast({
        title: 'Error',
        description: 'Please select a patient first',
        variant: 'destructive',
      });
      return;
    }

    if (!clinic?.id) {
      toast({
        title: 'Error',
        description: 'Clinic information is required',
        variant: 'destructive',
      });
      return;
    }

    setCreatingConversation(true);
    try {
      const response = await api.post('/patient-messaging', {
        clinicId: clinic.id,
        type: 'DIRECT',
        patientId: selectedPatient.id,
        patientName: selectedPatient.full_name,
        patientEmail: selectedPatient.email,
        patientPhone: selectedPatient.phone,
      }, {
        headers: { 'x-selected-clinic-id': clinic.id },
      });

      const joined = response.data?.joined === true;
      toast({
        title: joined ? 'Joined conversation' : 'Success',
        description: joined
          ? `Opened existing conversation with ${selectedPatient.full_name}`
          : `Conversation created with ${selectedPatient.full_name}!`,
      });
      setShowNewConversationDialog(false);
      setSelectedPatient(null);
      setPatientSearch('');
      setSearchResults([]);
      loadConversations(clinic.id);

      // Auto-select the (new or existing) conversation
      if (response.data) {
        setSelectedConversation(response.data);
        setMessages([]); // Clear any old messages
        setLoadingMessages(true);
        // Note: Messages are loaded by the useEffect that watches selectedConversation
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || error.message || 'Failed to create conversation',
        variant: 'destructive',
      });
    } finally {
      setCreatingConversation(false);
    }
  };

  // Open new conversation dialog
  const openNewConversationDialog = () => {
    setSelectedPatient(null);
    setPatientSearch('');
    setSearchResults([]);
    setShowNewConversationDialog(true);
  };

  // Send a message
  const userName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();

  const sendMessage = async () => {
    if (!selectedConversation || !newMessage.trim()) return;

    setSendingMessage(true);
    const messageBody = newMessage.trim();
    setNewMessage('');

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    // Derive display role from clinic context flags (matches backend getSenderInfo)
    const roles: string[] = [];
    if (isPractitioner) roles.push('provider');
    if (isAdmin) roles.push('clinic admin');
    if (roles.length === 0 && false) roles.push('staff');
    const displayRole = roles.length > 0 ? roles.join(' + ') : 'staff';

    // Optimistic UI update - add message immediately
    const tempMessageId = `temp-${Date.now()}`;
    const optimisticMessage: Message = {
      messageId: tempMessageId,
      body: messageBody,
      authorId: user?.id || '',
      authorType: 'staff',
      senderName: userName || 'Staff',
      senderRole: displayRole,
      createdAt: new Date().toISOString(),
      conversationId: getConvId(selectedConversation),
    };

    setMessages(prev => {
      const updated = [...prev, optimisticMessage].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
      return updated;
    });

    try {
      if (selectedConversation.twilioSid && isConnected && twilioConvRef.current) {
        // Send via Twilio SDK for real-time delivery to patient (with sender attributes)
        const msgBuilder = twilioConvRef.current.prepareMessage();
        msgBuilder.setBody(messageBody);
        msgBuilder.setAttributes({
          authorName: userName || 'Staff',
          authorRole: displayRole,
          authorType: 'staff',
        });
        await msgBuilder.build().send();
        setMessages(prev => prev.filter(m => m.messageId !== tempMessageId));
        setSendingMessage(false);
        textareaRef.current?.focus();
        // Persist to PostgreSQL in the background so clinic-side queries stay in sync
        api.post(`/patient-messaging/${getConvId(selectedConversation)}/messages`, {
          body: messageBody,
          skipTwilio: true, // Already sent via SDK — don't double-send
        }, {
          headers: { 'x-selected-clinic-id': selectedConversation.clinicId || clinic?.id },
        }).catch(persistError => {
          console.warn('[Messaging] Failed to persist Twilio message to DB:', persistError);
        });
      } else {
        await api.post(`/patient-messaging/${getConvId(selectedConversation)}/messages`, {
          body: messageBody,
        }, {
          headers: { 'x-selected-clinic-id': selectedConversation.clinicId || clinic?.id },
        });
        setMessages(prev => prev.filter(m => m.messageId !== tempMessageId));
        loadMessages(getConvId(selectedConversation));
      }
    } catch (error: any) {
      console.error('[Messaging] Failed to send message:', error);
      setMessages(prev => prev.filter(m => m.messageId !== tempMessageId));
      setNewMessage(messageBody);
      toast({
        title: 'Error',
        description: error.response?.data?.error || error.message || 'Failed to send message',
        variant: 'destructive',
      });
    } finally {
      setSendingMessage(false);
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
      });
    }
  };

  // Select a conversation
  const selectConversation = (conv: Conversation) => {
    setSelectedConversation(conv);
    setMessages([]); // Clear old messages immediately
    prevMessageCountRef.current = 0; // Reset so next load scrolls instantly
    setLoadingMessages(true); // Show loading right away
    setTypingParticipants([]);
    // Mark as read when provider opens the conversation
    markConversationAsRead(getConvId(conv));
    // Note: Messages are loaded by the useEffect that watches selectedConversation
  };

  // Get display name for conversation
  const getConversationDisplayName = (conv: Conversation) => {
    if (conv.patientName) return conv.patientName;
    // For legacy conversations without patient name, show date/time
    const date = new Date(conv.createdAt);
    return `Conversation - ${date.toLocaleDateString()}`;
  };

  // Format date like WhatsApp: Today, Yesterday, or MM/DD/YYYY
  const formatConversationDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const msgDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    if (msgDate.getTime() === today.getTime()) return 'Today';
    if (msgDate.getTime() === yesterday.getTime()) return 'Yesterday';

    const diffDays = Math.floor((today.getTime() - msgDate.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'long' });

    return date.toLocaleDateString([], { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  // Get initials for avatar
  const getInitials = (name?: string) => {
    if (!name) return 'C';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  // Send magic link to patient
  const sendLinkToPatient = async () => {
    if (!selectedConversation) return;

    setSendingLink(true);
    setLinkSent(false);
    
    try {
      const response = await api.post(`/patient-messaging/${getConvId(selectedConversation)}/send-link`, {
        channel: sendLinkChannel,
      }, {
        headers: { 'x-selected-clinic-id': selectedConversation.clinicId || clinic?.id },
      });

      if (response.data.success) {
        setLinkSent(true);
        toast({
          title: 'Link Sent',
          description: `Chat link sent via ${sendLinkChannel === 'both' ? 'email and SMS' : sendLinkChannel}.`,
        });
        
        // Auto close after showing success
        setTimeout(() => {
          setShowSendLinkDialog(false);
          setLinkSent(false);
        }, 2000);
      } else {
        throw new Error(response.data.message || response.data.error || 'Failed to send link');
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to send link to patient',
        variant: 'destructive',
      });
    } finally {
      setSendingLink(false);
    }
  };

  // Open send link dialog
  const openSendLinkDialog = () => {
    setSendLinkChannel('email');
    setLinkSent(false);
    setShowSendLinkDialog(true);
  };

  useEffect(() => {
    if (!clinic?.id) {
      setConversations([]);
      setSelectedConversation(null);
      setMessages([]);
      setTypingParticipants([]);
      return;
    }

    setSelectedConversation(null);
    setMessages([]);
    setTypingParticipants([]);
    loadConversations(clinic.id);
    fetchUnreadCounts();
  }, [clinic?.id, fetchUnreadCounts]);

  const connectionDisplay = getConnectionStatusDisplay(connectionStatus);
  const ConnectionIcon = connectionDisplay.icon;

  return (
    <div className="flex flex-col h-full">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h1 className="font-montserrat text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">Messaging</h1>
          <p className="mt-1 text-sm text-slate-500">Secure patient communications</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-slate-200 bg-white text-xs">
            <div className={`w-1.5 h-1.5 rounded-full ${connectionDisplay.color}`} />
            <ConnectionIcon className="h-3 w-3 text-slate-500" />
            <span className="text-slate-600 font-medium">{connectionDisplay.label}</span>
          </div>
          
          {!isConnected && connectionStatus === 'failed' && (
            <Button variant="outline" size="sm" onClick={() => {
              twilioClientRef.current?.shutdown();
              twilioClientRef.current = null;
              twilioConvRef.current = null;
              setConnectionStatus('disconnected');
            }} className="h-8">
              Reconnect
            </Button>
          )}
          
          <Button variant="outline" size="sm" onClick={() => loadConversations(clinic?.id)} className="h-9">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
            Refresh
          </Button>
          <Button size="sm" onClick={openNewConversationDialog} className="h-9">
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            New Conversation
          </Button>
        </div>
      </div>

      {/* Unread Messages Alert Banner */}
      {Object.keys(unreadCounts).length > 0 && (
        <div className="mb-4 p-3 rounded-md border border-blue-200 bg-blue-50 text-blue-800">
          <div className="flex items-center gap-2">
            <BellRing className="h-4 w-4 text-blue-600 animate-bounce" />
            <span className="text-sm font-medium">
              You have {Object.values(unreadCounts).reduce((a, b) => a + b, 0)} unread message{Object.values(unreadCounts).reduce((a, b) => a + b, 0) !== 1 ? 's' : ''} across {Object.keys(unreadCounts).length} conversation{Object.keys(unreadCounts).length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      )}

      {/* Module Status Banner */}
      {moduleStatus && (
        <div className={`mb-4 p-2.5 rounded-md border text-sm ${moduleStatus.status === 'ok' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-amber-50 text-amber-800 border-amber-200'}`}>
          <div className="flex items-center gap-2">
            <MessageSquare className="h-3.5 w-3.5" />
            <span className="font-medium text-xs">
              Messaging Module: {moduleStatus.status === 'ok' ? 'Connected' : 'Connecting...'}
            </span>
            {isConnected && (
              <Badge variant="outline" className="ml-auto bg-emerald-100 text-emerald-700 border-emerald-300 text-[10px] px-1.5 py-0">
                Real-time Active
              </Badge>
            )}
          </div>
        </div>
      )}

      <div className="flex gap-4 flex-1 min-h-0">
        {/* Conversations List */}
        <Card className="w-80 flex flex-col border border-slate-200">
          <CardHeader className="pb-3 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold text-slate-700">Conversations</CardTitle>
              {Object.keys(unreadCounts).length > 0 && (
                <Badge className="bg-red-500 hover:bg-red-600 text-white text-[10px] px-1.5 py-0 animate-pulse">
                  {Object.values(unreadCounts).reduce((a, b) => a + b, 0)} unread
                </Badge>
              )}
            </div>
            <CardDescription className="text-xs">{conversations.length} active</CardDescription>
          </CardHeader>
          <CardContent className="flex-1 p-0">
            <ScrollArea className="h-[500px]">
              {loading ? (
                <div className="flex items-center justify-center p-4">
                  <Loader2 className="h-5 w-5 animate-spin text-brand-teal" />
                </div>
              ) : conversations.length === 0 ? (
                <div className="p-4 text-center text-slate-400">
                  <MessageSquare className="h-7 w-7 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No conversations yet</p>
                  <Button variant="link" size="sm" onClick={openNewConversationDialog} className="mt-1 h-7 text-xs">
                    Start one
                  </Button>
                </div>
              ) : (
                <div className="space-y-0.5 p-2">
                  {[...conversations].sort((a, b) => {
                    // Sort: unread conversations first, then by most recent
                    const aUnread = unreadCounts[getConvId(a)] || 0;
                    const bUnread = unreadCounts[getConvId(b)] || 0;
                    if (aUnread > 0 && bUnread === 0) return -1;
                    if (aUnread === 0 && bUnread > 0) return 1;
                    // Within same group, sort by most recent
                    const aTime = new Date(a.lastMessageAt || a.createdAt).getTime();
                    const bTime = new Date(b.lastMessageAt || b.createdAt).getTime();
                    return bTime - aTime;
                  }).map((conv) => {
                    const convUnread = unreadCounts[getConvId(conv)] || 0;
                    return (
                      <button
                        key={getConvId(conv)}
                        type="button"
                        aria-label={`Conversation with ${getConversationDisplayName(conv)}${convUnread > 0 ? `, ${convUnread} unread` : ''}`}
                        className={`w-full text-left p-2.5 rounded-md cursor-pointer transition-colors ${
                          selectedConversation && getConvId(selectedConversation) === getConvId(conv)
                            ? 'bg-brand-teal/10 border border-brand-teal/20'
                            : convUnread > 0
                              ? 'bg-blue-50/60 hover:bg-blue-50 border border-blue-100'
                              : 'hover:bg-slate-50 border border-transparent'
                        }`}
                        onClick={() => selectConversation(conv)}
                      >
                        <div className="flex items-center gap-2.5">
                          <div className="relative">
                            <Avatar className="h-9 w-9">
                              <AvatarFallback className={`text-xs font-semibold ${convUnread > 0 ? 'bg-blue-100 text-blue-700' : 'bg-brand-teal/10 text-brand-teal'}`}>
                                {getInitials(conv.patientName)}
                              </AvatarFallback>
                            </Avatar>
                            {convUnread > 0 && (
                              <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[16px] h-4 px-1 text-[10px] font-bold rounded-full bg-red-500 text-white ring-2 ring-white">
                                {convUnread > 9 ? '9+' : convUnread}
                              </span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm truncate ${convUnread > 0 ? 'font-semibold text-slate-900' : 'font-medium text-slate-900'}`}>
                              {getConversationDisplayName(conv)}
                            </p>
                            <p className="text-[11px] text-slate-500 truncate flex items-center gap-1">
                              {onlinePatients.has(getConvId(conv)) ? (
                                <>
                                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                                  <span className="text-emerald-600">Online</span>
                                </>
                              ) : (
                                formatConversationDate(conv.lastMessageAt || conv.createdAt)
                              )}
                            </p>
                          </div>
                          {convUnread > 0 && (
                            <div className="shrink-0">
                              <Badge className="bg-blue-600 hover:bg-blue-700 text-white text-[10px] px-1.5 py-0">
                                New
                              </Badge>
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Messages Panel */}
        <Card className="flex-1 flex flex-col border border-slate-200">
          {selectedConversation ? (
            <>
              <CardHeader className="border-b border-slate-100 pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-9 w-9">
                      <AvatarFallback className="bg-brand-teal/10 text-brand-teal text-xs font-semibold">
                        {getInitials(selectedConversation.patientName)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <CardTitle className="text-sm font-semibold text-slate-900">
                        {getConversationDisplayName(selectedConversation)}
                      </CardTitle>
                      <CardDescription className="text-xs">
                        {selectedPatientDefaultEmail || selectedConversation.patientEmail || selectedConversation.patientPhone || `ID: ${getConvId(selectedConversation).slice(0, 12)}...`}
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={openSendLinkDialog}
                      className="h-8 text-xs"
                    >
                      <Link2 className="h-3.5 w-3.5 mr-1.5" />
                      Send Link
                    </Button>
                    {onlinePatients.has(getConvId(selectedConversation)) ? (
                      <Badge className="text-[10px] bg-emerald-100 text-emerald-700 hover:bg-emerald-100 gap-1">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        Online
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-[10px]">Offline</Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="flex-1 p-0 flex flex-col overflow-hidden">
                <div className="flex-1 overflow-y-auto p-3" style={{ maxHeight: 'calc(100vh - 320px)' }}>
                  {loadingMessages ? (
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="h-6 w-6 animate-spin text-brand-teal" />
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-slate-400">
                      <p className="text-sm">No messages yet. Start the conversation!</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {messages.map((msg) => {
                        // Only current user's messages on the right
                        const isCurrentUser = msg.authorId === user?.id;

                        const color = getMsgColorForAuthor(msg.authorId);

                        // Determine if staff or patient for display name
                        const isPatient = msg.authorType === 'patient' || msg.authorId?.includes('patient:');
                        const displayName = isPatient
                          ? `${msg.senderName || selectedConversation?.patientName || 'Patient'} (patient)`
                          : `${msg.senderName || 'Staff'}${msg.senderRole ? ` (${msg.senderRole})` : ''}`;
                        const initials = isPatient
                          ? getMsgInitials(msg.senderName || selectedConversation?.patientName)
                          : (msg.senderName ? getMsgInitials(msg.senderName) : getMsgInitials(`${user?.firstName || ''} ${user?.lastName || ''}`));

                        return (
                          <div
                            key={msg.messageId}
                            className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
                          >
                            <div className={`flex gap-2 max-w-[70%] ${isCurrentUser ? 'flex-row-reverse' : ''}`}>
                              <Avatar className="h-7 w-7 flex-shrink-0">
                                <AvatarFallback className={`${isCurrentUser ? 'bg-primary/10 text-primary' : `${color.bg} ${color.text}`} text-[10px] font-semibold`}>
                                  {initials}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p
                                  className={`text-[11px] font-semibold mb-0.5 ${
                                    isCurrentUser ? 'text-right text-primary' : color.name
                                  }`}
                                >
                                  {displayName}
                                </p>
                                <div
                                  className={`rounded-lg px-3 py-2 text-sm ${
                                    isCurrentUser
                                      ? 'bg-primary text-primary-foreground rounded-br-sm'
                                      : 'bg-slate-100 text-slate-900 rounded-bl-sm'
                                  }`}
                                >
                                  <p className="whitespace-pre-wrap break-words">{msg.body}</p>
                                </div>
                                <p className={`text-[11px] mt-1 text-slate-400 ${isCurrentUser ? 'text-right' : ''}`}>
                                  {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </div>
                
                {/* Typing indicator */}
                {typingParticipants.length > 0 && (
                  <div className="px-3 py-1.5 text-xs text-slate-500 animate-pulse">
                    <span className="inline-flex items-center gap-1">
                      <span className="flex gap-0.5">
                        <span className="w-1 h-1 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-1 h-1 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-1 h-1 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </span>
                      <span className="ml-1">
                        {selectedConversation.patientName || 'Someone'} is typing...
                      </span>
                    </span>
                  </div>
                )}
                
                <div className="p-3 border-t border-slate-100">
                  <div className="flex gap-2 items-end">
                    <div className="flex-1 relative">
                      <Textarea
                        ref={textareaRef}
                        placeholder="Type a message..."
                        value={newMessage}
                        onChange={handleTextareaChange}
                        onKeyDown={handleKeyDown}
                        disabled={sendingMessage}
                        className="min-h-[40px] max-h-[120px] resize-none text-sm"
                        rows={1}
                      />
                    </div>
                    <Button 
                      onClick={sendMessage} 
                      disabled={sendingMessage || !newMessage.trim()}
                      className="h-10 w-10 p-0"
                      size="icon"
                    >
                      {sendingMessage ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1.5">
                    Press Enter to send • Shift+Enter for new line
                    {isConnected && <span className="ml-2 text-emerald-600">• Real-time</span>}
                  </p>
                </div>
              </CardContent>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-400">
              <div className="text-center">
                <MessageSquare className="h-10 w-10 mx-auto mb-3 opacity-50" />
                <p className="text-sm font-medium text-slate-600">Select a conversation</p>
                <p className="text-xs mt-1">Choose from the list or start a new one</p>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* New Conversation Dialog */}
      <Dialog open={showNewConversationDialog} onOpenChange={(open) => {
        setShowNewConversationDialog(open);
        if (!open) {
          // Reset state when dialog closes
          setPatientSearch('');
          setSelectedPatient(null);
          setSearchResults([]);
        }
      }}>
        <DialogContent className="sm:max-w-lg flex flex-col max-h-[85vh]">
          <DialogHeader>
            <DialogTitle>New Conversation</DialogTitle>
            <DialogDescription>
              Search for a patient to start a conversation with.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden flex flex-col space-y-3 py-3">
            {/* Patient Search */}
            <div className="space-y-1.5 flex-shrink-0">
              <Label className="text-xs font-medium text-slate-700">Search Patient</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <Input
                  placeholder="Search by name, email, or phone..."
                  value={patientSearch}
                  onChange={(e) => setPatientSearch(e.target.value)}
                  className="pl-9 h-9 text-sm"
                />
              </div>
            </div>

            {/* Patient List - Scrollable container */}
            <div className="flex-1 min-h-0 overflow-y-auto">
              {searchingPatients ? (
                <div className="space-y-1.5 p-1.5">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="p-2.5 rounded-md border flex items-center gap-2.5">
                      <Skeleton className="h-9 w-9 rounded-full" />
                      <div className="flex-1 space-y-1.5">
                        <Skeleton className="h-3.5 w-[130px]" />
                        <Skeleton className="h-3 w-[180px]" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : searchResults.length > 0 ? (
                <div className="space-y-1.5 p-1.5">
                  <Label className="text-[11px] text-slate-500 px-1">
                    {patientSearch ? 'Search results' : 'Select a patient'} ({searchResults.length})
                  </Label>
                  {searchResults.map((patient) => (
                    <div
                      key={patient.id}
                      className={`p-2.5 rounded-md border cursor-pointer transition-colors ${
                        selectedPatient?.id === patient.id
                          ? 'border-brand-teal bg-brand-teal/5'
                          : 'hover:bg-slate-50 border-slate-200'
                      }`}
                      onClick={() => setSelectedPatient(patient)}
                    >
                      <div className="flex items-center gap-2.5">
                        <Avatar className="h-9 w-9">
                          <AvatarFallback className="bg-brand-teal/10 text-brand-teal text-[10px] font-semibold">
                            {patient.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'P'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900">
                            {patient.full_name || 'Unknown Patient'}
                          </p>
                          <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
                            {patient.dob && (
                              <span>DOB: {formatDOB(patient.dob)}</span>
                            )}
                            {patient.gender && (
                              <span className="capitalize">• {patient.gender}</span>
                            )}
                          </div>
                          <p className="text-[11px] text-slate-500 truncate">
                            {patient.email}
                          </p>
                        </div>
                        {selectedPatient?.id === patient.id && (
                          <Check className="h-4 w-4 text-brand-teal" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : patientSearch ? (
                <div className="text-center py-10 text-slate-400">
                  <User className="h-7 w-7 mx-auto mb-2 opacity-50" />
                  <p className="text-xs">No patients found matching "{patientSearch}"</p>
                </div>
              ) : (
                <div className="text-center py-10 text-slate-400">
                  <User className="h-7 w-7 mx-auto mb-2 opacity-50" />
                  <p className="text-xs">No patients available</p>
                </div>
              )}
            </div>
          </div>

          {/* Selected Patient Display */}
          {selectedPatient && (
            <div className="p-2.5 rounded-md bg-brand-teal/5 border border-brand-teal/20 flex-shrink-0">
              <div className="flex items-center gap-2.5">
                <Avatar className="h-9 w-9">
                  <AvatarFallback className="bg-brand-teal text-white text-[10px] font-semibold">
                    {selectedPatient.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'P'}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-sm font-medium text-slate-900">
                    {selectedPatient.full_name || 'Unknown Patient'}
                  </p>
                  <p className="text-[11px] text-slate-500">
                    {selectedPatient.dob && `DOB: ${formatDOB(selectedPatient.dob)}`}
                    {selectedPatient.dob && selectedPatient.email && ' • '}
                    {selectedPatient.email}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="flex-shrink-0 border-t border-slate-200 pt-3">
            <Button variant="outline" onClick={() => setShowNewConversationDialog(false)} className="h-9 text-sm">
              Cancel
            </Button>
            <Button 
              onClick={createConversation} 
              disabled={!selectedPatient || creatingConversation}
              className="h-9 text-sm"
            >
              {creatingConversation ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
                  Start Conversation
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Link to Patient Dialog */}
      <Dialog open={showSendLinkDialog} onOpenChange={setShowSendLinkDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Send Chat Link to Patient</DialogTitle>
            <DialogDescription>
              Send a secure link so the patient can access this conversation. The link expires in 24 hours.
            </DialogDescription>
          </DialogHeader>
          
          {linkSent ? (
            <div className="flex flex-col items-center py-6">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-4">
                <Check className="h-6 w-6 text-green-600" />
              </div>
              <p className="font-medium text-green-600">Link Sent Successfully!</p>
              <p className="text-sm text-muted-foreground mt-1">
                The patient will receive the link via {sendLinkChannel === 'both' ? 'email and SMS' : sendLinkChannel}.
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Send via</Label>
                  <RadioGroup 
                    value={sendLinkChannel} 
                    onValueChange={(v) => setSendLinkChannel(v as 'email' | 'sms' | 'both')}
                  >
                    <div className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer">
                      <RadioGroupItem value="email" id="email" />
                      <Label htmlFor="email" className="flex items-center gap-2 cursor-pointer flex-1">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="font-medium">Email</p>
                          <p className="text-xs text-muted-foreground">Send link to patient's email</p>
                        </div>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer">
                      <RadioGroupItem value="sms" id="sms" />
                      <Label htmlFor="sms" className="flex items-center gap-2 cursor-pointer flex-1">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="font-medium">SMS</p>
                          <p className="text-xs text-muted-foreground">Send link via text message</p>
                        </div>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer">
                      <RadioGroupItem value="both" id="both" />
                      <Label htmlFor="both" className="flex items-center gap-2 cursor-pointer flex-1">
                        <div className="flex -space-x-1">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <Phone className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="font-medium">Both</p>
                          <p className="text-xs text-muted-foreground">Send via email and SMS</p>
                        </div>
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowSendLinkDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={sendLinkToPatient} disabled={sendingLink}>
                  {sendingLink ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Link2 className="h-4 w-4 mr-2" />
                      Send Link
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Messaging;
